import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { Form } from "@/components/ui/form";
import { Diff } from "@/features/FormCard/Diff";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
} from "@heroui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ConditionalAdditionalFields } from "@/features/FormCard/DynamicCard";
import {
  AutocompleteField,
  AutocompleteLabelField,
  DatePickerField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";
import { detailedInformationByAssetClass2 } from "./data/detailedInformationByAssetClass2";

import { useDynamicForm } from "@/features/Edit/dynamic-edit-form";
import { FormActionsUpdate } from "@/features/FormCard/Actions";
import {
  ConfirmationModal,
  DeleteConfirmationModal,
} from "@/features/FormCard/Modals";

import { ExternalLink } from "@/components/ExternalLink";
import { Label } from "@radix-ui/react-label";
import { dynamicFormConfig, dynamicFormFields } from "./edit-form-conf";
import { TechnicalInfoCardStatic } from "./technical_info_card";
import { fieldTranslations } from "./translations";
import { Tables } from "@/gen/tableNames/tables";
import { Clients } from "@/lib/clients";

export function N1StaticDatumsEditCard({
  id,
  client,
}: {
  id: number;
  client: string;
}) {
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    isDeleting,
    changedFields,
    isLoadingTranslations,
    options,
    handleDelete,
    translations,
  } = useDynamicForm({
    id,
    client,
    table_name: Tables.N1_STATIC_DATA,
    formConfig: dynamicFormConfig,
    fieldsNotApplyTranslation: [dynamicFormFields.asset_class_2],
    adjustOnReset: (key, value, fetchedData) => {
      if (key === "concentrated_asset") {
        return {
          text: value,
        };
      } else if (key === dynamicFormFields.n1_issuer_name) {
        return {
          text: fetchedData.n1_issuer_name,
          id: fetchedData.issuer_id,
        };
      }
      return value;
    },
  });

  const currencyOptions = useCurrencyOptions();

  const name = form.watch(dynamicFormFields.name);
  const assetClass2Key = form.watch(dynamicFormFields.asset_class_2);

  const detailedInformationByAssetClass2WithTranslations = useMemo(
    () => detailedInformationByAssetClass2(t),
    [t],
  );

  return (
    <>
      <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
        <div className="flex justify-center w-full">
          <Card className="p-4 bg-white w-full ">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <CardHeader className="font-bold text-gray-700">
                  {t(`${Tables.N1_STATIC_DATA}.title`, {
                    ns: "tables",
                    defaultValue: "Actif",
                  })}{" "}
                  {id}
                </CardHeader>
                <Divider />
                <CardBody className="flex flex-col gap-4">
                  <Label>
                    {t(
                      `${Tables.N1_STATIC_DATA}.sections.general_information`,
                      {
                        ns: "tables",
                        defaultValue: "Informations générales",
                      },
                    )}
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <div className="md:col-span-8">
                      <InputField
                        name={dynamicFormFields.name}
                        label={t(`${Tables.N1_STATIC_DATA}.columns.name`, {
                          ns: "tables",
                          defaultValue: "Nom",
                        })}
                        isRequired={true}
                        control={form.control}
                      />
                    </div>
                    <div className="md:col-span-4">
                      <AutocompleteLabelField
                        name={dynamicFormFields.currency}
                        label={t(`${Tables.N1_STATIC_DATA}.columns.currency`, {
                          ns: "tables",
                          defaultValue: "Devise",
                        })}
                        options={currencyOptions}
                        isRequired={true}
                        control={form.control}
                        className="min-w-0"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                    <InputField
                      className="md:col-span-4"
                      name={dynamicFormFields.isin}
                      label={t(`${Tables.N1_STATIC_DATA}.columns.isin`, {
                        ns: "tables",
                        defaultValue: "ISIN",
                      })}
                      control={form.control}
                    />
                    <InputField
                      className="md:col-span-4"
                      name={dynamicFormFields.code}
                      label={t(`${Tables.N1_STATIC_DATA}.columns.code`, {
                        ns: "tables",
                        defaultValue: "Code",
                      })}
                      control={form.control}
                    />
                    <InputField
                      className="md:col-span-4"
                      name={dynamicFormFields.ticker}
                      label={t(`${Tables.N1_STATIC_DATA}.columns.ticker`, {
                        ns: "tables",
                        defaultValue: "Ticker",
                      })}
                      control={form.control}
                    />

                    <AutocompleteField
                      className="md:col-span-6"
                      name={dynamicFormFields.asset_class_1}
                      label={t(
                        `${Tables.N1_STATIC_DATA}.columns.asset_class_1`,
                        {
                          ns: "tables",
                        },
                      )}
                      options={options?.[dynamicFormFields.asset_class_1] ?? []}
                      control={form.control}
                      isRequired={true}
                    />
                    <AutocompleteField
                      className="md:col-span-6"
                      name={dynamicFormFields.asset_class_2}
                      label={t(
                        `${Tables.N1_STATIC_DATA}.columns.asset_class_2`,
                        {
                          ns: "tables",
                        },
                      )}
                      options={options?.[dynamicFormFields.asset_class_2] ?? []}
                      control={form.control}
                      returnSelectedKey={true}
                      isRequired={true}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SelectField
                      name={dynamicFormFields.product}
                      label={t(`${Tables.N1_STATIC_DATA}.columns.product`, {
                        ns: "tables",
                        defaultValue: "Produit",
                      })}
                      options={translations?.[dynamicFormFields.product]}
                      control={form.control}
                      allow_empty_option={true}
                    />
                    <SelectField
                      name={dynamicFormFields.sector}
                      label={t(`${Tables.N1_STATIC_DATA}.columns.sector`, {
                        ns: "tables",
                        defaultValue: "Secteur",
                      })}
                      options={translations?.[dynamicFormFields.sector]}
                      control={form.control}
                      allow_empty_option={true}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SelectField
                      name={dynamicFormFields.region}
                      label={t(`${Tables.N1_STATIC_DATA}.columns.region`, {
                        ns: "tables",
                        defaultValue: "Région",
                      })}
                      options={translations?.[dynamicFormFields.region]}
                      control={form.control}
                      allow_empty_option={true}
                    />
                    <InputField
                      name="category"
                      label={t(`${Tables.N1_STATIC_DATA}.columns.category`, {
                        ns: "tables",
                        defaultValue: "Catégorie",
                      })}
                      control={form.control}
                    />
                  </div>
                  <ConditionalAdditionalFields
                    fieldKey={assetClass2Key}
                    control={form.control}
                    isLoadingTranslations={isLoadingTranslations}
                    fieldsToDisplayByKey={
                      detailedInformationByAssetClass2WithTranslations
                    }
                  />
                  <Divider />
                  <Label>{t("sections.lpt_simulation")}</Label>
                  <SelectField
                    name={dynamicFormFields.lpt_liquidity}
                    label={t(`${Tables.N1_STATIC_DATA}.columns.lpt_liquidity`, {
                      ns: "tables",
                      defaultValue: "Liquidités",
                    })}
                    options={translations?.[dynamicFormFields.lpt_liquidity]}
                    control={form.control}
                    allow_empty_option={true}
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DatePickerField
                      name={dynamicFormFields.lpt_fund_vintage_start_date}
                      label={t(
                        `${Tables.N1_STATIC_DATA}.columns.lpt_fund_vintage_start_date`,
                        { ns: "tables", defaultValue: "Millésime" },
                      )}
                      control={form.control}
                    />
                    <DatePickerField
                      name={dynamicFormFields.lpt_investment_period_end_date}
                      label={t(
                        `${Tables.N1_STATIC_DATA}.columns.lpt_investment_period_end_date`,
                        {
                          ns: "tables",
                          defaultValue: "Fin de la période d'investissement",
                        },
                      )}
                      control={form.control}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      name={dynamicFormFields.lpt_investment_duration}
                      label={t(
                        `${Tables.N1_STATIC_DATA}.columns.lpt_investment_duration`,
                        {
                          ns: "tables",
                          defaultValue: "Durée d'investissement",
                        },
                      )}
                      control={form.control}
                    />
                    <InputField
                      name={dynamicFormFields.lpt_net_percentage_capital_called}
                      label={t(
                        `${Tables.N1_STATIC_DATA}.columns.lpt_net_percentage_capital_called`,
                        {
                          ns: "tables",
                          defaultValue: "Pourcentage net du capital appelé",
                        },
                      )}
                      control={form.control}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      name={dynamicFormFields.lpt_loan_to_value}
                      label={t(
                        `${Tables.N1_STATIC_DATA}.columns.lpt_loan_to_value`,
                        {
                          ns: "tables",
                          defaultValue: "Ratio prêt-valeur",
                        },
                      )}
                      control={form.control}
                    />
                    <InputField
                      name={dynamicFormFields.lpt_expected_distribution_rate}
                      label={t(
                        `${Tables.N1_STATIC_DATA}.columns.lpt_expected_distribution_rate`,
                        {
                          ns: "tables",
                          defaultValue: "Taux de distribution prévu",
                        },
                      )}
                      control={form.control}
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InputField
                      name={dynamicFormFields.lpt_expected_investment_return}
                      label={t(
                        `${Tables.N1_STATIC_DATA}.columns.lpt_expected_investment_return`,
                        {
                          ns: "tables",
                          defaultValue: "Rendement d'investissement prévu",
                        },
                      )}
                      control={form.control}
                    />
                    <InputField
                      name={dynamicFormFields.lpt_expected_annual_fee_rate}
                      label={t(
                        `${Tables.N1_STATIC_DATA}.columns.lpt_expected_annual_fee_rate`,
                        {
                          ns: "tables",
                          defaultValue: "Frais annuels attendus",
                        },
                      )}
                      control={form.control}
                    />
                  </div>
                </CardBody>
                <CardFooter>
                  <FormActionsUpdate
                    onReset={handleReset}
                    onDelete={openDeleteModal}
                    isDirty={form.formState.isDirty}
                    elementId={id}
                    client={client}
                    allowDelete={false}
                  />
                </CardFooter>
              </form>
            </Form>
          </Card>
          {name && (
            <div className="absolute top-[3.20rem] right-12 2xl:right-10 lg:flex gap-2 flex-row hidden">
              <Button
                href={toWorkspacePath(
                  `/admin/${Clients.N1}/${Tables.N1_TRANSACTIONS}`,
                )}
                queryParams={{ n1_static_data_id_equal: id }}
                variant="bordered"
                color="primary"
                showAnchorIcon
                as={ExternalLink}
                radius="full"
                className="bg-white border-1 border-neutral-300 shadow-xl"
              >
                {t("actions.transactions")}
              </Button>
              <Button
                href={toWorkspacePath(
                  `/admin/${Clients.N1}/${Tables.N1_TIME_SERIES}`,
                )}
                queryParams={{ n1_static_data_id_equal: id }}
                variant="bordered"
                color="primary"
                showAnchorIcon
                as={ExternalLink}
                radius="full"
                className="bg-white border-1 border-neutral-300 shadow-xl"
              >
                {t("actions.valuations")}
              </Button>
            </div>
          )}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
            children={
              changedFields
                ? Diff(
                    changedFields,
                    [dynamicFormFields.issuer_id],
                    fieldTranslations,
                    Tables.N1_STATIC_DATA,
                  )
                : t("no_changes")
            }
          />

          <DeleteConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onDelete={handleDelete}
            isLoading={isDeleting}
          />
        </div>
      </div>
      <TechnicalInfoCardStatic
        id={Number(id)}
        table_name={Tables.N1_STATIC_DATA}
        strategy_id_control={form.control}
        client={client}
      />
    </>
  );
}
